import { DST_STATES } from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/utils';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import React, { useCallback, useEffect, useState } from 'react';
import TimeRemaining from 'aquilla/icons/misc/TimeRemaining';
import { getShortFormTimeFormat } from '@utils/dates';
import { useSWRConfig } from 'swr';
import { getDSTEndpoint } from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/apiHelper';
import { useRouter } from 'next/router';
import usePreciseTimer from '@hooks/usePreciseTimer';
import Image from '@base/FutureImage';

const BannerContainer = styled.div`
  background: rgba(253, 242, 205, 0.4);
  background-repeat: no-repeat;
  background-position: right top;

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const BannerContentWrapper = styled.div`
  width: var(--layout-width);
  margin: 0 auto;

  ${mediaQuery.uptoTablet} {
    width: var(--layout-mweb-width);
  }
`;

const BannerContent = styled.div`
  padding: 51.5px 0;
  background-image: url('${getImage('/dst/ongoingTest.svg')}');
  background-repeat: no-repeat;
  background-position: right;
`;

const ButtonWrapper = styled.div``;

const StyledButton = styled(Button)`
  width: auto;
`;

const SubHeader = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  margin: ${Spacings.SPACING_2B} 0 ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    align-items: start;
    width: 100%;
    margin: ${Spacings.SPACING_0B};
  }
`;

const MwebBannerContent = styled.div`
  width: 100%;
  display: flex;
`;

const MwebBannerContentContainer = styled.div`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
`;

const MwebDstBannerLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_4B};
`;

const MwebDstBannerTopContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
`;

const ResumeButton = styled.div`
  display: flex;
  width: ${Spacings.SPACING_30B};
  height: ${Spacings.SPACING_8B};
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.HEAVY};
  color: var(--color-i-white);
  background-color: var(--color-i-black);
  align-items: center;
  justify-content: center;
  border-radius: ${Spacings.SPACING_1B};
`;

const MwebSummaryWrapper = styled.div`
  margin: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_9B};
  width: calc(100% - 32px);
  border-top: ${Spacings.SPACING_1} solid var(--color-i-divider);
  display: flex;
  flex-direction: row;
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  align-items: center;
  justify-content: center;
`;

const getButtonLabel = (dstState) => {
  switch (dstState) {
    case DST_STATES.RESUME:
      return 'Resume';
    case DST_STATES.ATTEMPTED:
      return 'View attempt summary';
    default:
      break;
  }
};

const Attempted = ({ block, onClick, clickHandlers }) => {
  const { data, extraBlockInfo } = block;
  const { header, subHeader } = extraBlockInfo;
  const { finishTime = 0, state: dstState, quizUid, sessionUid } = data[0];
  const {
    query: { goalUID }
  } = useRouter();

  const [timeRemaining, setTime] = useState(
    Math.round((new Date(finishTime).getTime() - new Date().getTime()) / 1000)
  );

  const handler = useCallback(() => {
    setTime((time) => time - 1);
  }, []);

  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (timeRemaining === 0) mutate(getDSTEndpoint(goalUID));
  }, [timeRemaining, mutate, goalUID]);

  usePreciseTimer(handler, 1000, true);

  const handleClick = useCallback(() => {
    if (onClick) onClick();
    switch (dstState) {
      case DST_STATES.RESUME:
        return clickHandlers.takeTest();

      case DST_STATES.ATTEMPTED:
        return clickHandlers.viewAttempts(quizUid, sessionUid);

      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dstState, quizUid, sessionUid]);

  return (
    <>
      <BannerContainer>
        <BannerContentWrapper>
          <BannerContent>
            <Typography variant="h3">{header}</Typography>
            {dstState === DST_STATES.RESUME && (
              <SubHeader>
                <TimeRemaining
                  height={20}
                  width={20}
                  color="var(--color-i-red)"
                />
                <Typography color="var(--color-i-red)" variant="h5">
                  {`Expires in ${getShortFormTimeFormat(timeRemaining)}`}
                </Typography>
              </SubHeader>
            )}
            {dstState === DST_STATES.ATTEMPTED && (
              <SubHeader>
                <Typography color="var(--color-text-secondary)" variant="p1">
                  {subHeader}
                </Typography>
              </SubHeader>
            )}
            <ButtonWrapper>
              <StyledButton
                label={getButtonLabel(dstState)}
                variant={
                  dstState === DST_STATES.ATTEMPTED ? 'secondary' : 'primary'
                }
                size="large"
                onClick={handleClick}
              />
            </ButtonWrapper>
          </BannerContent>
        </BannerContentWrapper>
      </BannerContainer>
      <MwebBannerContentContainer>
        {dstState === DST_STATES.RESUME && (
          <MwebBannerContent>
            <MwebDstBannerLeftContainer>
              <MwebDstBannerTopContainer>
                <Typography color="var(--color-i-red)" variant="h5">
                  {`Expires in ${getShortFormTimeFormat(timeRemaining)}`}
                </Typography>
                <Typography variant="h3">{header}</Typography>
              </MwebDstBannerTopContainer>
              <ResumeButton onClick={handleClick}>Resume now</ResumeButton>
            </MwebDstBannerLeftContainer>
            <Image
              height={152}
              width={112}
              src="dst/unattempteddst.svg"
              alt="default"
            />
          </MwebBannerContent>
        )}
        {dstState === DST_STATES.ATTEMPTED && (
          <>
            <MwebBannerContent>
              <MwebDstBannerLeftContainer>
                <MwebDstBannerTopContainer>
                  <Typography variant="h3">{header}</Typography>
                  <SubHeader>
                    <Typography
                      color="var(--color-text-secondary)"
                      variant="p1"
                    >
                      {subHeader}
                    </Typography>
                  </SubHeader>
                </MwebDstBannerTopContainer>
              </MwebDstBannerLeftContainer>
              <Image
                height={120}
                width={120}
                src="dst/attempteddst.svg"
                alt="default"
              />
            </MwebBannerContent>
            <MwebSummaryWrapper onClick={handleClick}>
              VIEW SUMMARY OF YOUR ATTEMPT
              <RightChevron width={16} height={16} />
            </MwebSummaryWrapper>
          </>
        )}
      </MwebBannerContentContainer>
    </>
  );
};

export default Attempted;
